import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Typography, InputBase, Paper, Stack, List, ListItemButton, ListItemText, Divider, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tooltip, Button, CircularProgress, Avatar, Link, Menu, MenuItem, GlobalStyles
} from '@mui/material';
import Joyride, { STATUS, EVENTS } from 'react-joyride';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SendIcon from '@mui/icons-material/Send';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AutoGraphIcon from '@mui/icons-material/AutoGraph'; // pour "analyser"
import CloseIcon from '@mui/icons-material/Close'; // pour fermer le dialog
import PlayArrowIcon from '@mui/icons-material/PlayArrow'; // Activer
import StopIcon from '@mui/icons-material/Stop'; // Désactiver
import SegmentIcon from '@mui/icons-material/Segment'; // Segment
import ListIcon from '@mui/icons-material/List'; // Liste
import DescriptionIcon from '@mui/icons-material/Description'; // Décrire
import ContentCopyIcon from '@mui/icons-material/ContentCopy'; // Dupliquer
import ScienceIcon from '@mui/icons-material/Science'; // AB Test
import CampaignIcon from '@mui/icons-material/Campaign'; // Campagne
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'; // Comparer
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'; // Tour guide


import {
  sendQueryToAI, analyzeSegment, analyzeCampaignPerformance, detectIntent, compareCampaigns,
  getUserChats, createChat, getChatMessages, createMessage, updateChat, deleteChat, createAbTestViaLLM,
  queryKnowledgeBase, generateCampaignFromPrompt, createCampaignFromJson,
  suggestSegments, createSegment, createSegmentDirect, fetchUserCredentials, createTestCampaign,
  toggleCampaignPublishStatus, deleteCampaign, cloneCampaign
} from '../../services/chatService';
import api from '../../services/auth';
import { getCurrentUser, logout } from "../../services/auth";

// Debounce hook for search optimization
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Memoized chat list item component
const ChatListItem = React.memo(({ chat, isActive, onSelect, onRename, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(chat.title);
  const [justFinishedEditing, setJustFinishedEditing] = useState(false);
  const inputRef = useRef(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRename = () => {
    handleMenuClose();
    setEditText(chat.title);
    setIsEditing(true);
  };

  const handleDelete = (event) => {
    handleMenuClose();
    onDelete(event, chat.id);
  };

  const handleSaveEdit = async () => {
    if (editText.trim() && editText.trim() !== chat.title) {
      // Call the onRename function with the new title
      await onRename({ id: chat.id, title: editText.trim() });
    }
    setIsEditing(false);
    setJustFinishedEditing(true);
    
    // Remove any focus from the input or its parent to prevent background styling
    if (inputRef.current) {
      inputRef.current.blur();
    }
    // Also remove focus from the document's active element if it's related
    if (document.activeElement && document.activeElement.tagName === 'INPUT') {
      document.activeElement.blur();
    }
    
    // Clear the "just finished editing" state after a brief delay
    setTimeout(() => {
      setJustFinishedEditing(false);
    }, 200);
  };

  const handleCancelEdit = () => {
    setEditText(chat.title);
    setIsEditing(false);
    setJustFinishedEditing(true);
    
    // Remove any focus to prevent background styling
    if (inputRef.current) {
      inputRef.current.blur();
    }
    if (document.activeElement && document.activeElement.tagName === 'INPUT') {
      document.activeElement.blur();
    }
    
    // Clear the "just finished editing" state after a brief delay
    setTimeout(() => {
      setJustFinishedEditing(false);
    }, 200);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit();
    }
  };

  return (
  <ListItemButton
    selected={isActive && !isEditing} // Remove selection styling during editing
      onClick={() => !isEditing && onSelect(chat)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{ 
        borderRadius: 2, 
        mb: 0.5, 
        fontFamily: 'Montserrat, Arial, sans-serif',
        position: 'relative',
        pr: isHovered && !isEditing ? 6 : 2, // Make room for menu button when hovered and not editing
        cursor: isEditing ? 'default' : 'pointer',
        // Prevent background styling during editing or just after editing
        ...(isEditing || justFinishedEditing) && {
          backgroundColor: 'transparent !important',
          '&:hover': {
            backgroundColor: 'transparent !important'
          },
          '&:focus': {
            backgroundColor: 'transparent !important'
          },
          '&:focus-visible': {
            backgroundColor: 'transparent !important'
          },
          '&:focus-within': {
            backgroundColor: 'transparent !important'
          },
          '&.Mui-selected': {
            backgroundColor: 'transparent !important'
          },
          '&.Mui-selected:hover': {
            backgroundColor: 'transparent !important'
          },
          '&.Mui-focusVisible': {
            backgroundColor: 'transparent !important'
          },
          '&.MuiButtonBase-root:focus': {
            backgroundColor: 'transparent !important'
          },
          '&.MuiListItemButton-root:focus': {
            backgroundColor: 'transparent !important'
          }
        }
      }}
    >
      {isEditing ? (
        <InputBase
          inputRef={inputRef}
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          onBlur={handleSaveEdit}
          onKeyDown={handleKeyDown}
          fullWidth
          sx={{
            fontSize: 15,
            fontWeight: isActive ? 700 : 500,
            color: isActive ? '#1976d2' : '#212529',
            fontFamily: 'Montserrat, Arial, sans-serif',
            padding: '4px 8px',
            border: '1px solid #1976d2',
            borderRadius: 1,
            backgroundColor: 'transparent',
            '& .MuiInputBase-input': {
              padding: 0,
              lineHeight: 1.5,
            }
          }}
        />
      ) : (
    <ListItemText
      primary={chat.title}
      primaryTypographyProps={{ 
        fontWeight: isActive ? 700 : 500, 
        fontSize: 15, 
        color: isActive ? '#1976d2' : '#212529', 
        fontFamily: 'Montserrat, Arial, sans-serif',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}
          sx={{ pr: 1 }} // Padding to ensure text doesn't overlap with menu button
        />
      )}
      
      {/* Menu button - appears on hover and not when editing */}
      {!isEditing && (
        <Box 
          sx={{ 
            position: 'absolute',
            right: 8,
            opacity: isHovered || open ? 1 : 0,
            transition: 'opacity 0.2s ease-in-out'
          }}
        >
          <Tooltip title="Options">
            <IconButton 
              size="small" 
              onClick={handleMenuClick}
              sx={{
                backgroundColor: isActive ? 'rgba(25, 118, 210, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                '&:hover': {
                  backgroundColor: isActive ? 'rgba(25, 118, 210, 0.2)' : 'rgba(0, 0, 0, 0.08)'
                }
              }}
            >
              <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
      )}

      {/* Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(0, 0, 0, 0.08)'
          }
        }}
      >
        <MenuItem onClick={(e) => { e.stopPropagation(); handleRename(); }} sx={{ fontSize: 14, fontFamily: 'Montserrat, Arial, sans-serif' }}>
          <EditIcon fontSize="small" sx={{ mr: 1.5, color: '#666' }} />
          Renommer
        </MenuItem>
        <MenuItem 
          onClick={(e) => { e.stopPropagation(); handleDelete(e); }} 
          sx={{ 
            fontSize: 14, 
            fontFamily: 'Montserrat, Arial, sans-serif',
            color: '#d32f2f',
            '&:hover': {
              backgroundColor: 'rgba(211, 47, 47, 0.08)'
            }
          }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1.5, color: '#d32f2f' }} />
          Supprimer
        </MenuItem>
      </Menu>
  </ListItemButton>
  );
});

// Helper function to parse and render text with clickable segment links
const parseSegmentLinks = (text, navigate) => {
  // Regex to match markdown-style links: [text](#segment-segmentName)
  const linkRegex = /\[([^\]]+)\]\(#segment-([^)]+)\)/g;
  
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(text)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    
    // Add the clickable link
    const linkText = match[1]; // Text inside [...]
    const segmentName = decodeURIComponent(match[2]); // Segment name after #segment-
    
    parts.push(
      <Link 
        key={match.index}
        component="button"
        variant="body1"
        onClick={(e) => {
          e.preventDefault();
          // Navigate to segment page and populate search using state
          navigate('/segment', { 
            state: { 
              searchTerm: segmentName,
              source: 'chat' 
            } 
          });
        }}
        sx={{
          color: 'primary.main',
          textDecoration: 'underline',
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontSize: 'inherit',
          fontFamily: 'inherit',
          '&:hover': {
            color: 'primary.dark',
          }
        }}
      >
        {linkText}
      </Link>
    );
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text after the last link
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  
  return parts.length > 0 ? parts : text;
};

// Memoized message component
const MessageBubble = React.memo(({ message, index }) => {
  const navigate = useNavigate();
  const isTypingIndicator = message.from === 'bot' && message.text === '...';

  return (
  <Box display="flex" flexDirection="column" width="100%">
    <Box display="flex" flexDirection={message.from === 'user' ? 'row-reverse' : 'row'} alignItems="flex-end" gap={1}>
      <Avatar sx={{ 
        bgcolor: message.from === 'user' ? '#1976d2' : '#43a047', 
        width: { xs: 28, sm: 32 }, 
        height: { xs: 28, sm: 32 }, 
        fontWeight: 700, 
        fontFamily: 'Montserrat, Arial, sans-serif',
        fontSize: { xs: '0.7rem', sm: '0.8rem' }
      }}>
        {message.from === 'user' ? 'ME' : 'IA'}
      </Avatar>
      <Paper elevation={0} sx={{ 
        background: message.from === 'user' ? '#e3f0fc' : '#e8f5e9', 
        px: 2, py: 1, borderRadius: 2, 
        fontFamily: 'Montserrat, Arial, sans-serif', 
        fontSize: { xs: 14, sm: 15 }, 
        color: '#212529', 
        maxWidth: { xs: '85%', sm: '75%', md: '70%' }, 
        whiteSpace: 'pre-line',
        wordBreak: 'break-word' // Ensure long words don't overflow
      }}>
        {isTypingIndicator ? (
          <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} sx={{ height: '22.5px' }}>
            <Box className="typing-dot" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'grey.500' }} />
            <Box className="typing-dot" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'grey.500', animationDelay: '0.2s' }} />
            <Box className="typing-dot" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'grey.500', animationDelay: '0.4s' }} />
          </Box>
        ) : (
          parseSegmentLinks(message.text, navigate)
        )}
      </Paper>
    </Box>
  </Box>
  );
});

const typingAnimation = `
  @keyframes blink {
    0% { opacity: 0.2; }
    20% { opacity: 1; }
    100% { opacity: 0.2; }
  }

  .typing-dot {
    animation: blink 1.4s infinite both;
  }
`;

const Chat = () => {
  const currentUser = getCurrentUser();
  const location = useLocation();
  const navigate = useNavigate();
  
  // All hooks must be called before any conditional returns
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isLoadingChats, setIsLoadingChats] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isBotReplying, setIsBotReplying] = useState(false);
  const [search, setSearch] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 1200); // Responsive sidebar default
  const chatEndRef = useRef(null);
  // State for handling confirmation actions (like deleting a campaign)
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(null);
  
  // Campaign clone with rename state
  const [pendingClone, setPendingClone] = useState(null);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneNewName, setCloneNewName] = useState('');
  const [cloneNameError, setCloneNameError] = useState(false);
  
  // Quota exceeded modal state
  const [quotaExceededOpen, setQuotaExceededOpen] = useState(false);
  const [quotaErrorMessage, setQuotaErrorMessage] = useState('');
  
  // Flag to prevent race condition when creating new chat with first message
  const [isCreatingFirstMessage, setIsCreatingFirstMessage] = useState(false);
  
  // Tour state
  const [runTour, setRunTour] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);

  // Debounced search to avoid filtering on every keystroke
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    // Pré-remplir le champ de saisie si un prompt est passé via la navigation
    if (location.state?.prompt) {
      setUserInput(location.state.prompt);
      // Nettoyer l'état pour éviter que le prompt ne réapparaisse au rechargement
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  // Memoized filtered chats to prevent unnecessary recalculations
  const filteredChats = useMemo(() => 
    chats.filter((chat) =>
      chat.title.toLowerCase().includes(debouncedSearch.toLowerCase())
    ), [chats, debouncedSearch]
  );

  // Fetch all chats on component mount
  useEffect(() => {
    const fetchChats = async () => {
      try {
        const userChats = await getUserChats();
        // Sort chats by updated_at (most recent first) to ensure new chats always appear at top
        const sortedChats = [...userChats].sort((a, b) => {
          const dateA = new Date(a.updated_at || a.created_at || 0);
          const dateB = new Date(b.updated_at || b.created_at || 0);
          return dateB - dateA; // Descending order (newest first)
        });
        setChats(sortedChats);
        if (sortedChats.length > 0) {
          setActiveChat(sortedChats[0]);
        } else {
          // Pour un nouvel utilisateur sans conversation, s'assurer que rien n'est actif
          // pour afficher le message de bienvenue.
          setActiveChat(null);
          setMessages([]);
        }
      } catch (error) {
        console.error('Failed to fetch chats:', error);
      } finally {
        setIsLoadingChats(false);
      }
    };
    fetchChats();
  }, []);

  // Fetch messages when the active chat ID changes (not when title or other properties change)
  useEffect(() => {
    // Skip fetching if we're in the process of creating a new chat with first message
    if (isCreatingFirstMessage) {
      return;
    }
    
    if (activeChat?.id) {
      const fetchMessages = async () => {
        setIsLoadingMessages(true);
        try {
          const chatMessages = await getChatMessages(activeChat.id);
          // Convert messages to match the new format
          const formattedMessages = chatMessages.map(msg => ({
            from: msg.role === 'user' ? 'user' : 'bot',
            text: msg.content,
            timestamp: new Date(msg.timestamp || Date.now())
          }));
          setMessages(formattedMessages);
        } catch (error) {
          console.error(`Failed to fetch messages for chat ${activeChat.id}:`, error);
        } finally {
          setIsLoadingMessages(false);
        }
      };
      fetchMessages();
    } else {
      setMessages([]);
    }
  }, [activeChat?.id, isCreatingFirstMessage]);

  // Scroll to the bottom of the chat on new messages - debounced
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [messages.length]); // Only depend on length, not entire messages array

  // Memoized callback functions to prevent unnecessary re-renders
  const handleNewChat = useCallback(async () => {
    // Create a chat with a default name only if user wants to start a chat without sending a message
    try {
      const newChat = await createChat('Nouvelle Conversation');
      setChats(prev => [newChat, ...prev]);
      setActiveChat(newChat);
    } catch (error) {
      console.error('Failed to create new chat:', error);
    }
  }, []);

  const handleSelectChat = useCallback((chat) => {
    setActiveChat(chat);
  }, []);

  const handleDeleteChat = useCallback(async (e, chatId) => {
    e.stopPropagation();
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette conversation ?')) {
      try {
        await deleteChat(chatId);
        setChats(prev => {
          const updatedChats = prev.filter(c => c.id !== chatId);
          return updatedChats;
        });
        setActiveChat(prev => prev?.id === chatId ? null : prev);
      } catch (error) {
        console.error('Failed to delete chat:', error);
      }
    }
  }, []);

  const handleInlineRename = useCallback(async (chatData) => {
    if (!chatData.id || !chatData.title.trim()) return;
    try {
      const updatedChat = await updateChat(chatData.id, chatData.title.trim());
      setChats(prev => prev.map(c => c.id === chatData.id ? updatedChat : c));
      setActiveChat(prev => prev?.id === chatData.id ? updatedChat : prev);
    } catch (error) {
      console.error('Failed to rename chat:', error);
    }
  }, []);

  // Handle opening the clone dialog
  const handleOpenCloneDialog = useCallback((campaignName) => {
    setPendingClone(campaignName);
    setCloneNewName(`${campaignName} (Clone)`); // Default name suggestion
    setCloneNameError(false); // Reset any previous error
    setCloneDialogOpen(true);
  }, []);

  // Handle canceling the clone dialog
  const handleCloneCancel = useCallback(async () => {
    setCloneDialogOpen(false);
    
    // Add a message to inform the user that the operation was canceled
    const campaignName = pendingClone;
    if (campaignName && activeChat) {
      const botResponseText = `❌ Opération annulée. La campagne "${campaignName}" n'a pas été dupliquée.`;
      const botMessage = { from: 'bot', text: botResponseText, timestamp: new Date() };
      await createMessage(activeChat.id, 'bot', botResponseText);
      setMessages(prev => [...prev, botMessage]);
    }
    
    // Clear the clone state
    setPendingClone(null);
    setCloneNewName('');
  }, [pendingClone, activeChat]);

  // Handle confirming the clone with new name
  const handleCloneConfirm = useCallback(async () => {
    const campaignName = pendingClone;
    const newName = cloneNewName.trim();
    
    if (!newName) {
      // Show error in the dialog if the name is empty
      setCloneNameError(true);
      return;
    }
    
    setCloneDialogOpen(false);
    
    // Update message to show processing
    let botResponseText = `Duplication de la campagne "${campaignName}" avec le nouveau nom "${newName}" en cours...`;
    setMessages(prev => [...prev.filter(m => m.text !== `Duplication de la campagne "${campaignName}" en cours...`), 
      { from: 'bot', text: botResponseText, timestamp: new Date() }]);
    
    try {
      // Call the function to clone the campaign with new name
      const result = await cloneCampaign(campaignName, newName);
      
      // Update the message with the result
      if (result.success) {
        botResponseText = `✅ Campagne dupliquée avec succès ! La nouvelle campagne s'appelle "${result.newCampaignName}".`;
      } else {
        botResponseText = `❌ Erreur : ${result.message}`;
      }
      
      const currentChat = activeChat;
      const botMessage = { from: 'bot', text: botResponseText, timestamp: new Date() };
      await createMessage(currentChat.id, 'bot', botResponseText);
      setMessages(prev => [...prev.filter(m => !m.text.includes('Duplication de la campagne') || !m.text.includes('en cours')), botMessage]);
    } catch (error) {
      console.error('Error cloning campaign with new name:', error);
      botResponseText = `❌ Erreur technique lors de la duplication de la campagne : ${error.message}`;
      const currentChat = activeChat;
      const botMessage = { from: 'bot', text: botResponseText, timestamp: new Date() };
      await createMessage(currentChat.id, 'bot', botResponseText);
      setMessages(prev => [...prev.filter(m => !m.text.includes('Duplication de la campagne') || !m.text.includes('en cours')), botMessage]);
    } finally {
      setPendingClone(null);
      setCloneNewName('');
    }
  }, [pendingClone, cloneNewName, activeChat]);

  // Optimized input change handler
  const handleInputChange = useCallback((e) => {
    setUserInput(e.target.value);
  }, []);

  // Ref for chat input to focus when action is clicked
  const chatInputRef = useRef(null);

  // Available actions with their prompts
  const availableActions = useMemo(() => [
    {
      id: 'activate_campaign',
      label: 'Activer une campagne',
      icon: <PlayArrowIcon fontSize="small" />,
      prompt: 'Active la campagne [nom de la campagne]'
    },
    {
      id: 'delete_campaign',
      label: 'Supprimer une campagne',
      icon: <DeleteIcon fontSize="small" />,
      prompt: 'Supprime la campagne [nom de la campagne]'
    },
    {
      id: 'create_segment',
      label: 'Créer un segment',
      icon: <SegmentIcon fontSize="small" />,
      prompt: 'Crée un segment à partir des contacts [critères des contacts]'
    },
    {
      id: 'deactivate_campaign',
      label: 'Désactiver une campagne',
      icon: <StopIcon fontSize="small" />,
      prompt: 'Désactive la campagne [nom de la campagne]'
    },
    {
      id: 'compare_campaigns',
      label: 'Comparaison des campagnes',
      icon: <CompareArrowsIcon fontSize="small" />,
      prompt: 'Compare les campagnes [nom de la campagne 1] et [nom de la campagne 2]'
    },
    {
      id: 'create_campaign',
      label: 'Créer une campagne',
      icon: <CampaignIcon fontSize="small" />,
      prompt: 'Après l\'envoi du premier email intitulé [nom de l\'email 1], attendre [définissez une durée]; si le mail est ouvert, envoyer le mail de [nom de l\'email 2] avec une offre spéciale, [définissez une durée] puis envoyer un email [nom de l\'email 3] mettant en avant les nouveautés puis mettre fin au workflow'
    },
    {
      id: 'list_segments',
      label: 'Afficher tous les segments',
      icon: <ListIcon fontSize="small" />,
      prompt: 'Affiche la liste de tous mes segments existants'
    },
    {
      id: 'analyze_campaign',
      label: 'Analyser une campagne',
      icon: <AutoGraphIcon fontSize="small" />,
      prompt: 'Analyse la campagne [nom de la campagne] et donne-moi ses performances'
    },
    {
      id: 'describe_segment',
      label: 'Décrire un segment',
      icon: <DescriptionIcon fontSize="small" />,
      prompt: 'Décris le segment [nom du segment]'
    },
    {
      id: 'clone_campaign',
      label: 'Dupliquer une campagne',
      icon: <ContentCopyIcon fontSize="small" />,
      prompt: 'Duplique la campagne [nom de la campagne]'
    },
    {
      id: 'create_ab_test',
      label: 'Créer un AB TEST',
      icon: <ScienceIcon fontSize="small" />,
      prompt: 'Crées un AB TEST avec pour email parent [nom de l\'email], avec pour sujet [définissez un objet] pendant [définissez une durée]'
    }
  ], []);

  // Handle action click - prefilled the input and focus it
  const handleActionClick = useCallback((prompt) => {
    setUserInput(prompt);
    // Focus the input after a short delay to ensure it's rendered
    setTimeout(() => {
      if (chatInputRef.current) {
        // InputBase ref gives access to the input element
        const inputElement = chatInputRef.current.querySelector('input, textarea') || chatInputRef.current;
        if (inputElement) {
          inputElement.focus();
          // Scroll into view if needed
          inputElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          // Move cursor to end of text
          if (inputElement.setSelectionRange) {
            const length = prompt.length;
            inputElement.setSelectionRange(length, length);
          }
        }
      }
    }, 100);
  }, []);

  // Optimized search change handler
  const handleSearchChange = useCallback((e) => {
    setSearch(e.target.value);
  }, []);

  // Memoized message intent detection function using LLM
  const detectMessageIntent = useCallback(async (userMessageContent) => {
    try {
      // Check for segment creation intent first using improved pattern matching
      // This regex is more flexible and allows for words between the action and "segment"
      const segmentCreationRegex = /(create|créer|cree|crée|make|faire|build|construire|établir|establish|new|nouveau|nouvelle).*(segment|segmentation)/i;
      
      // Also check for specific segment creation phrases with more context
      const segmentCreationPhrases = [
        /crée?\s+(moi\s+)?(un\s+)?(nouveau\s+)?segment/i,
        /créer\s+(un\s+)?(nouveau\s+)?segment/i,
        /faire\s+(un\s+)?(nouveau\s+)?segment/i,
        /construire\s+(un\s+)?(nouveau\s+)?segment/i,
        /établir\s+(un\s+)?(nouveau\s+)?segment/i,
        /(create|make|build)\s+(a\s+)?(new\s+)?segment/i,
        /je\s+(veux|voudrais)\s+(un\s+)?segment/i,
        /i\s+want\s+(a\s+)?segment/i,
        // Handle the specific case that was failing
        /crée\s+moi\s+un\s+segment/i,
        /cree\s+moi\s+un\s+segment/i
      ];
      
      const isSegmentCreation = segmentCreationRegex.test(userMessageContent.trim()) || 
                               segmentCreationPhrases.some(phrase => phrase.test(userMessageContent.trim()));
      
      if (isSegmentCreation) {
        console.log("Detected segment creation request via improved regex pattern");
        return { 
          type: 'segment_creation', 
          message: userMessageContent,
          confidence: 0.95
        };
      }
      // Call the intent detection service
      const intentResult = await detectIntent(userMessageContent);
      console.log('DEBUG detectIntent API response:', intentResult);
      
      // Check if the intent detection returned a quota error in the reasoning
      if (intentResult.reasoning && 
          (intentResult.reasoning.includes("⚠️ Limite de quota API atteinte") ||
           intentResult.reasoning.toLowerCase().includes('quota') ||
           intentResult.reasoning.includes('429') ||
           intentResult.reasoning.toLowerCase().includes('error parsing llm response'))) {
        return { 
          type: 'quota_error', 
          message: userMessageContent, 
          detected_entity: null, 
          confidence: 0,
          error: "⚠️ Limite de quota API atteinte. Votre quota d'utilisation de l'API Google Gemini a été dépassé. Veuillez vérifier votre plan et vos détails de facturation, ou réessayer plus tard."
        };
      }
      // Mappe le résultat backend sur le format attendu
      switch (intentResult.intent) {
        case "campaign_create":
          return { type: 'campaign_create', message: userMessageContent, detected_entity: intentResult.detected_entity };
        case "segment_creation":
          console.log("Detected segment creation intent with confidence:", intentResult.confidence);
          return { 
            type: 'segment_creation', 
            message: userMessageContent, 
            confidence: intentResult.confidence,
            action_type: intentResult.action_type || 'create'
          };
        case "campaign_analysis":
          console.log("Detected campaign analysis intent with confidence:", intentResult.confidence);
          return { 
            type: 'campaign', 
            message: userMessageContent,
            campaign_name: intentResult.detected_entity,
            detected_entity: intentResult.detected_entity
          };
        case "campaign_comparison":
          console.log("Detected campaign comparison intent with confidence:", intentResult.confidence);
          return { 
            type: 'campaign_comparison', 
            message: userMessageContent,
            campaign_names: intentResult.detected_entities || [],
            action_type: intentResult.action_type,
            confidence: intentResult.confidence
          };
        case "campaign_toggle_publish":
          console.log("Detected campaign toggle publish intent with confidence:", intentResult.confidence);
          return {
            type: 'campaign_toggle_publish',
            message: userMessageContent,
            campaign_name: intentResult.campaign_name || intentResult.detected_entity,
            action: intentResult.action || (intentResult.action_type === 'activate' ? 'activate' : 'deactivate'),
            confidence: intentResult.confidence
          };
        case "campaign_delete":
          console.log("Detected campaign delete intent with confidence:", intentResult.confidence);
          return {
            type: 'campaign_delete',
            message: userMessageContent,
            campaign_name: intentResult.campaign_name || intentResult.detected_entity,
            confidence: intentResult.confidence
          };
        case "campaign_clone":
          console.log("Detected campaign clone intent with confidence:", intentResult.confidence);
          return {
            type: 'campaign_clone',
            message: userMessageContent,
            campaign_name: intentResult.campaign_name || intentResult.detected_entity,
            confidence: intentResult.confidence
          };
        case "segment_analysis":
          return { type: 'segment_analysis', message: userMessageContent, segment_name: intentResult.detected_entity, detected_entity: intentResult.detected_entity, action_type: intentResult.action_type, confidence: intentResult.confidence };
        case "segment_display":
          return { type: 'segment_display', message: userMessageContent, segment_name: intentResult.detected_entity, detected_entity: intentResult.detected_entity, action_type: intentResult.action_type, confidence: intentResult.confidence };
        case "segment_list":
          return { type: 'segment_list', message: userMessageContent, detected_entity: null, action_type: intentResult.action_type, confidence: intentResult.confidence };
        case "segment_count":
          return { type: 'segment_count', message: userMessageContent, segment_name: intentResult.detected_entity, detected_entity: intentResult.detected_entity, action_type: intentResult.action_type, confidence: intentResult.confidence };
        case "ab_test":
          return { type: 'ab_test', message: userMessageContent, abtest_details: intentResult.detected_entity };
        case "campaign_create_percent": {
          // Correction : mapping robuste pour garantir la présence des champs
          const email_name = intentResult.email_name || (intentResult.data && intentResult.data.email_name);
          const percent = intentResult.percent || (intentResult.data && intentResult.data.percent);
          const segment_name = intentResult.segment_name || (intentResult.data && intentResult.data.segment_name);
          console.log('DEBUG mapping campaign_create_percent:', { email_name, percent, segment_name, intentResult });
          return { type: 'campaign_create_percent', message: userMessageContent, email_name, percent, segment_name, ...intentResult };
        }
        default:
          console.log("Detected general query intent with confidence:", intentResult.confidence);
          // Check for segment creation intent via LLM result's reasoning or detected_entity
          const segmentCreationPatterns = [
            'create segment', 'créer segment', 'new segment', 'crée un segment', 
            'creer un segment', 'cree un segment', 'faire un segment',
            'crée moi un segment', 'cree moi un segment', 'créer moi un segment'
          ];
          
          const hasSegmentCreationInReasoning = intentResult.reasoning && 
            intentResult.reasoning.toLowerCase().includes('segment') && 
            (intentResult.reasoning.toLowerCase().includes('create') || 
             intentResult.reasoning.toLowerCase().includes('new') || 
             intentResult.reasoning.toLowerCase().includes('make') ||
             intentResult.reasoning.toLowerCase().includes('créer') ||
             intentResult.reasoning.toLowerCase().includes('crée'));
             
          const hasSegmentCreationInMessage = segmentCreationPatterns.some(pattern => 
            userMessageContent.toLowerCase().includes(pattern.toLowerCase()));
          
          if (hasSegmentCreationInReasoning || hasSegmentCreationInMessage) {
            console.log("Detected segment creation intent from general query fallback");
            return { 
              type: 'segment_creation', 
              message: userMessageContent,
              confidence: 0.8
            };
          }
          return { 
            type: 'general', 
            message: userMessageContent,
            detected_entity: null,
            confidence: intentResult.confidence || 0
          };
      }
    } catch (error) {
      console.error('Error in detectMessageIntent:', error);
      
      // Check if this is a quota error
      const intentErrorMessage = error.message || '';
      if (intentErrorMessage.includes("⚠️ Limite de quota API atteinte") || 
          intentErrorMessage.toLowerCase().includes('quota') ||
          intentErrorMessage.toLowerCase().includes('429') ||
          intentErrorMessage.toLowerCase().includes('rate limit')) {
        // Return a special quota error type so the main handler can display the proper message
        return { 
          type: 'quota_error', 
          message: userMessageContent, 
          detected_entity: null, 
          confidence: 0,
          error: "⚠️ Limite de quota API atteinte. Votre quota d'utilisation de l'API Google Gemini a été dépassé. Veuillez vérifier votre plan et vos détails de facturation, ou réessayer plus tard."
        };
      }
      
      return { type: 'general', message: userMessageContent, detected_entity: null, confidence: 0 };
    }
  }, []);

  // Handle confirmation responses (e.g., for campaign deletion)
  const handleConfirmation = useCallback(async (userResponse, confirmationAction, confirmationData) => {
    // Check if the user confirmed (yes, oui, confirm, etc.) or denied (no, non, cancel, etc.)
    const confirmationRegex = /^(yes|oui|confirm|confirmer|ok|d'accord|sure|absolutely|proceed|continuer|go ahead|valider)$/i;
    const denialRegex = /^(no|non|cancel|annuler|stop|nope|do not|don't)$/i;
    
    const isConfirmed = confirmationRegex.test(userResponse.trim());
    const isDenied = denialRegex.test(userResponse.trim()) || userResponse.toLowerCase().includes('pas') || userResponse.toLowerCase().includes('not');
    
    console.log('Processing confirmation:', { userResponse, isConfirmed, isDenied, confirmationAction, confirmationData });
    
    // If it's neither a clear confirmation nor denial, ask again
    if (!isConfirmed && !isDenied) {
      const clarificationText = "Je n'ai pas compris votre réponse. Veuillez répondre par 'oui' ou 'non'.";
      const clarificationMessage = { 
        from: 'bot', 
        text: clarificationText, 
        timestamp: new Date(),
        awaitingConfirmation: true,
        confirmationAction,
        confirmationData
      };
      
      await createMessage(activeChat.id, 'bot', clarificationText);
      setMessages(prev => [...prev, clarificationMessage]);
      return;
    }
    
    // Reset the awaiting confirmation state
    setAwaitingConfirmation(null);
    
    // If denied, just acknowledge and return
    if (isDenied) {
      const cancellationText = "D'accord, l'action a été annulée.";
      const cancellationMessage = { from: 'bot', text: cancellationText, timestamp: new Date() };
      await createMessage(activeChat.id, 'bot', cancellationText);
      setMessages(prev => [...prev, cancellationMessage]);
      return;
    }
    
    // If confirmed, perform the appropriate action based on the confirmation type
    switch (confirmationAction) {
      case 'delete_campaign':
        try {
          const { campaignName } = confirmationData;
          // Show processing message
          const processingText = `Suppression de la campagne "${campaignName}" en cours...`;
          const processingMessage = { from: 'bot', text: processingText, timestamp: new Date() };
          setMessages(prev => [...prev, processingMessage]);
          
          // Call the API to delete the campaign
          const result = await deleteCampaign(campaignName);
          
          // Remove the processing message and show the result
          setMessages(prev => prev.filter(m => m.text !== processingText));
          
          if (result.success) {
            const successText = `✅ Campagne "${campaignName}" supprimée avec succès.`;
            const successMessage = { from: 'bot', text: successText, timestamp: new Date() };
            await createMessage(activeChat.id, 'bot', successText);
            setMessages(prev => [...prev, successMessage]);
          } else {
            const errorText = `❌ Erreur lors de la suppression de la campagne : ${result.message}`;
            const errorMessage = { from: 'bot', text: errorText, timestamp: new Date() };
            await createMessage(activeChat.id, 'bot', errorText);
            setMessages(prev => [...prev, errorMessage]);
          }
        } catch (error) {
          console.error('Error handling campaign deletion confirmation:', error);
          const errorText = `❌ Erreur technique lors de la suppression de la campagne : ${error.message}`;
          const errorMessage = { from: 'bot', text: errorText, timestamp: new Date() };
          await createMessage(activeChat.id, 'bot', errorText);
          setMessages(prev => [...prev, errorMessage]);
        }
        break;
      
      default:
        console.warn(`Unknown confirmation action: ${confirmationAction}`);
        break;
    }
  }, [activeChat]);

  const handleKeyDown = useCallback(async (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      await handleSendMessage();
    }
  }, [userInput, activeChat, chats, isBotReplying]); // Add dependencies

  const handleSendMessage = useCallback(async () => {
    if (userInput.trim() === '' || isBotReplying) return;
  
    let currentChat = activeChat;
    let isNewChat = false;
    
    // Check if we're waiting for confirmation
    if (awaitingConfirmation) {
      // Add the user message to the chat
      const userMessage = { from: 'user', text: userInput, timestamp: new Date() };
      setMessages(prev => [...prev, userMessage]);
      await createMessage(currentChat.id, 'user', userInput);
      setUserInput('');
      
      // Process the confirmation
      await handleConfirmation(
        userInput, 
        awaitingConfirmation.action, 
        awaitingConfirmation.data
      );
      return;
    }
    
    // If there's no active chat, create a new one first
    if (!currentChat) {
      try {
        // Set flag to prevent race condition
        setIsCreatingFirstMessage(true);
        
        let chatTitle = userInput;
        if (chatTitle.length > 40) {
          chatTitle = chatTitle.substring(0, 40).split(' ').slice(0, -1).join(' ');
          chatTitle += '...';
        }
        const newChat = await createChat(chatTitle || 'Nouvelle Conversation');
        setChats(prev => [newChat, ...prev]);
        setActiveChat(newChat);
        currentChat = newChat;
        isNewChat = true;
      } catch (error) {
        console.error('Failed to create chat on the fly:', error);
        setIsCreatingFirstMessage(false); // Reset flag on error
        return;
      }
    }
  
    const userMessageContent = userInput;
    setUserInput('');
  
    // Add user message to state immediately for responsiveness
    const userMessage = { from: 'user', text: userMessageContent, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
  
    // Save user message to backend with quota check
    try {
      await createMessage(currentChat.id, 'user', userMessageContent);
    } catch (error) {
      // Check if this is a quota exceeded error
      if (error.response?.status === 403 && error.response?.data?.detail?.error === 'chat_requests') {
        // Remove the user message from state since it wasn't saved
        setMessages(prev => prev.filter(m => m !== userMessage));
        // Show quota exceeded modal
        setQuotaErrorMessage(error.response.data.detail.message);
        setQuotaExceededOpen(true);
        // Reset input to original message so user can retry later
        setUserInput(userMessageContent);
        // Reset flags
        if (isNewChat) {
          setIsCreatingFirstMessage(false);
        }
        return;
      }
      // Re-throw other errors
      throw error;
    }
  
    // If this is the first message in a chat with the default title, update the chat title
    if (currentChat.title === 'Nouvelle Conversation') {
      let newTitle = userMessageContent;
      if (newTitle.length > 40) {
        newTitle = newTitle.substring(0, 40).split(' ').slice(0, -1).join(' ');
        newTitle += '...';
      }
      try {
        const updatedChat = await updateChat(currentChat.id, newTitle);
        setChats(prev => prev.map(c => c.id === currentChat.id ? updatedChat : c));
        setActiveChat(updatedChat);
        currentChat = updatedChat;
      } catch (error) {
        console.error('Failed to update chat title after first message:', error);
      }
    }
  
    // First, check for simple greetings to avoid unnecessary API calls
    const simpleGreetingRegex = /^(hello|hi|hey|bonjour|salut|coucou|hola|hallo|yo|greetings|good morning|good afternoon|good evening|howdy|sup|what's up|whatsup|hey there|hi there)[\s!.?]*$/i;
    if (simpleGreetingRegex.test(userMessageContent.trim())) {
      const isFrenchGreeting = /bonjour|salut|coucou/i.test(userMessageContent.trim());
      const greeting = isFrenchGreeting 
        ? "Bonjour ! Comment puis-je vous aider aujourd'hui ? Vous pouvez me demander d'analyser un segment, d'afficher les détails d'une campagne ou poser une question sur vos données."
        : "Hello! How can I help you today? You can ask me to analyze a segment, show campaign details, or ask a question about your data.";
      
      const botMessage = { from: 'bot', text: greeting, timestamp: new Date() };
      
      // No need to show typing indicator for a quick greeting
      setMessages(prev => [...prev, botMessage]);
      await createMessage(currentChat.id, 'bot', greeting);
      
      // Reset flag if this was a new chat
      if (isNewChat) {
        setIsCreatingFirstMessage(false);
      }
      
      return; // Exit early
    }

    setIsBotReplying(true);

    // Add typing indicator
    const botTypingMessage = { from: 'bot', text: '...', timestamp: new Date() };
    setMessages(prev => [...prev, botTypingMessage]);
  
    try {
      // Nouvelle détection d'intention via LLM
      const messageAnalysis = await detectMessageIntent(userMessageContent);
      
      // Handle quota errors from intent detection
      if (messageAnalysis.type === 'quota_error') {
        const botMessage = { 
          from: 'bot', 
          text: messageAnalysis.error, 
          timestamp: new Date()
        };
        await createMessage(currentChat.id, 'bot', messageAnalysis.error);
        setMessages(prev => [...prev.filter(m => m.text !== '...'), botMessage]);
        setIsBotReplying(false);
        if (isNewChat) {
          setIsCreatingFirstMessage(false);
        }
        return;
      }
      
      if (messageAnalysis.type === 'segment_creation') {
        // Segment creation - direct flow without confirmation
        console.log("Creating segment directly from chat message");
        
        try {
          // Get credentials from API to ensure we have the latest
          let hasMauticCredentials = false;
          try {
            const credentials = await fetchUserCredentials();
            const mauticUrl = credentials.mautic_url || '';
            const mauticUsername = credentials.mautic_username || '';
            const mauticPassword = credentials.mautic_password || '';
            hasMauticCredentials = !!(mauticUrl && mauticUsername && mauticPassword);
            console.log(`Segment creation - Mautic credentials from API: ${hasMauticCredentials ? 'Available' : 'Not available'}`);
          } catch (credError) {
            console.error('Could not fetch credentials from API:', credError);
            // Continue with creation, the createSegmentDirect function will handle the lack of credentials
          }
          
          // Log user brand info for debugging
          const authData = JSON.parse(localStorage.getItem('user')) || {};
          const userBrand = authData.brand || 'batira';
          console.log(`Segment creation - Using brand: ${userBrand}`);
          
          const creationResult = await createSegmentDirect(userMessageContent);
          
          // Use the formatted message from the backend
          const botResponseText = creationResult.message;
          
          // Add help text if Mautic credentials are missing
          let fullResponseText = botResponseText;
          if (!hasMauticCredentials && creationResult.success) {
            fullResponseText += '\n\nℹ️ Pour créer des segments dans Mautic, veuillez ajouter vos identifiants Mautic dans les paramètres de votre profil.';
          }
          
          const botMessage = { from: 'bot', text: fullResponseText, timestamp: new Date(), type: 'segment_creation' };
          await createMessage(currentChat.id, 'bot', fullResponseText);
          setMessages(prev => [...prev.filter(m => m.text !== '...'), botMessage]);
        } catch (err) {
          console.error('Error in segment creation flow:', err);
          
          // Check if the error message is already properly formatted
          const errorMessage = err.message || "Unknown error";
          let errorText;
          
          // If the error message is already formatted (starts with emoji or mentions quota/API/timeout), use it as-is
          if (errorMessage.match(/^[⚠️❌✅]/) || 
              errorMessage.toLowerCase().includes('quota') ||
              errorMessage.toLowerCase().includes('limite') ||
              errorMessage.toLowerCase().includes('api') ||
              errorMessage.toLowerCase().includes('token') ||
              errorMessage.toLowerCase().includes('billing') ||
              errorMessage.toLowerCase().includes('exceeded') ||
              errorMessage.toLowerCase().includes('serveur') ||
              errorMessage.toLowerCase().includes('accessible') ||
              errorMessage.toLowerCase().includes('temps') ||
              errorMessage.toLowerCase().includes('timeout')) {
            errorText = errorMessage;
          } else if (errorMessage.toLowerCase().includes('timed out') || 
                     errorMessage.toLowerCase().includes('read timeout') || 
                     errorMessage.toLowerCase().includes('connectionpool')) {
            // Handle raw timeout errors that weren't processed by the backend
            errorText = '❌ La création du segment a pris trop de temps. Le segment pourrait avoir été créé. Veuillez vérifier et réessayer si nécessaire.';
          } else {
            // Otherwise, detect language and wrap with appropriate prefix
            const isUserQueryFrench = userMessageContent.match(/[àáâäæçèéêëîïôœùûüÿ]/i) || 
                             /créer|nouveau|nouvelle|segment/.test(userMessageContent.toLowerCase());
                             
            errorText = isUserQueryFrench
              ? `❌ Erreur lors de la création du segment: ${errorMessage}`
              : `❌ Error creating segment: ${errorMessage}`;
          }
            
          const errorBotMessage = { 
            from: 'bot', 
            text: errorText, 
            timestamp: new Date()
          };
          await createMessage(currentChat.id, 'bot', errorText);
          setMessages(prev => [...prev.filter(m => m.text !== '...'), errorBotMessage]);
        }
        
        setIsBotReplying(false);
        if (isNewChat) {
          setIsCreatingFirstMessage(false);
        }
        return;
      }
      
      if (messageAnalysis.type === 'ab_test') {
        // Appel direct à l'API d'A/B test
        const abTestResult = await createAbTestViaLLM(messageAnalysis.message);
        let botResponseText = "I'm not sure I understand your request. Could you reformulate or provide more details about what you're looking for?";
        
        if (abTestResult.success) {
          botResponseText = `✅ Variante A/B test créée avec succès !`;
        } else {
          let errorMsg = abTestResult.error || abTestResult.message || abTestResult.errors;

          // Si c'est un tableau, prends le message du premier objet
          if (Array.isArray(errorMsg)) {
            errorMsg = errorMsg[0]?.message || JSON.stringify(errorMsg[0]) || JSON.stringify(errorMsg);
          }

          // Si c'est un objet, prends le champ message
          if (typeof errorMsg === 'object') {
            errorMsg = errorMsg.message || JSON.stringify(errorMsg);
          }

          // Si c'est une string qui contient un JSON, essaie d'extraire le message
          if (typeof errorMsg === 'string') {
            const jsonMatch = errorMsg.match(/\{.*\}/s);
            if (jsonMatch) {
              try {
                const parsed = JSON.parse(jsonMatch[0]);
                if (parsed.errors && Array.isArray(parsed.errors) && parsed.errors[0]?.message) {
                  errorMsg = parsed.errors[0].message;
                } else if (parsed.message) {
                  errorMsg = parsed.message;
                }
              } catch (e) {
                // ignore si le JSON n'est pas valide
              }
            }
          }

          botResponseText = `❌ Erreur lors de la création de l'A/B test : ${errorMsg}`;
        }
        const botMessage = { from: 'bot', text: botResponseText, timestamp: new Date() };
        await createMessage(currentChat.id, 'bot', botResponseText);
        setMessages(prev => [...prev.filter(m => m.text !== '...'), botMessage]);
        setIsBotReplying(false);
        if (isNewChat) {
          setIsCreatingFirstMessage(false);
        }
        return;
      }
      
      // --- AJOUT : gestion de l'activation/désactivation de campagnes ---
      if (messageAnalysis.type === 'campaign_toggle_publish') {
        console.log('Campaign toggle publish intent detected:', messageAnalysis);
        // Extract campaign name and action from intent detection
        const campaignName = messageAnalysis.campaign_name;
        const action = messageAnalysis.action;
        
        if (!campaignName) {
          const botResponseText = 'Je n\'ai pas compris le nom de la campagne. Pourriez-vous préciser quelle campagne vous souhaitez activer ou désactiver ?';
          const botMessage = { from: 'bot', text: botResponseText, timestamp: new Date() };
          await createMessage(currentChat.id, 'bot', botResponseText);
          setMessages(prev => [...prev.filter(m => m.text !== '...'), botMessage]);
          setIsBotReplying(false);
          if (isNewChat) {
            setIsCreatingFirstMessage(false);
          }
          return;
        }
        
        // Initialize message to show it's processing
        let botResponseText = `${action === 'activate' ? 'Activation' : 'Désactivation'} de la campagne "${campaignName}" en cours...`;
        setMessages(prev => [...prev.filter(m => m.text !== '...'), { from: 'bot', text: botResponseText, timestamp: new Date() }]);
        
        try {
          // Call the function to toggle campaign status
          const isPublished = (action === 'activate');
          const result = await toggleCampaignPublishStatus(campaignName, isPublished);
          
          // Update the message with the result
          if (result.success) {
            botResponseText = `✅ ${isPublished ? 'Activation' : 'Désactivation'} réussie : ${result.message}`;
          } else {
            botResponseText = `❌ Erreur : ${result.message}`;
          }
          
          const botMessage = { from: 'bot', text: botResponseText, timestamp: new Date() };
          await createMessage(currentChat.id, 'bot', botResponseText);
          setMessages(prev => [...prev.filter(m => m.text !== `${action === 'activate' ? 'Activation' : 'Désactivation'} de la campagne "${campaignName}" en cours...`), botMessage]);
          setIsBotReplying(false);
          if (isNewChat) {
            setIsCreatingFirstMessage(false);
          }
          return;
        } catch (error) {
          console.error('Error toggling campaign status:', error);
          botResponseText = `❌ Erreur technique lors de ${action === 'activate' ? 'l\'activation' : 'la désactivation'} de la campagne "${campaignName}" : ${error.message}`;
          const botMessage = { from: 'bot', text: botResponseText, timestamp: new Date() };
          await createMessage(currentChat.id, 'bot', botResponseText);
          setMessages(prev => [...prev.filter(m => m.text !== `${action === 'activate' ? 'Activation' : 'Désactivation'} de la campagne "${campaignName}" en cours...`), botMessage]);
          setIsBotReplying(false);
          if (isNewChat) {
            setIsCreatingFirstMessage(false);
          }
          return;
        }
      }
      
      // --- AJOUT : gestion de la suppression de campagnes ---
      if (messageAnalysis.type === 'campaign_delete') {
        console.log('Campaign delete intent detected:', messageAnalysis);
        // Extract campaign name from intent detection
        const campaignName = messageAnalysis.campaign_name;
        
        if (!campaignName) {
          const botResponseText = 'Je n\'ai pas compris le nom de la campagne. Pourriez-vous préciser quelle campagne vous souhaitez supprimer ?';
          const botMessage = { from: 'bot', text: botResponseText, timestamp: new Date() };
          await createMessage(currentChat.id, 'bot', botResponseText);
          setMessages(prev => [...prev.filter(m => m.text !== '...'), botMessage]);
          setIsBotReplying(false);
          if (isNewChat) {
            setIsCreatingFirstMessage(false);
          }
          return;
        }
        
        // First, ask for confirmation before deleting
        let botResponseText = `Voulez-vous vraiment supprimer la campagne "${campaignName}" ? Cette action est irréversible.`;
        const confirmationMessage = { from: 'bot', text: botResponseText, timestamp: new Date(), awaitingConfirmation: true, confirmationAction: 'delete_campaign', confirmationData: { campaignName } };
        await createMessage(currentChat.id, 'bot', botResponseText);
        setMessages(prev => [...prev.filter(m => m.text !== '...'), confirmationMessage]);
        
        // Store that we're waiting for confirmation in the state
        setAwaitingConfirmation({
          action: 'delete_campaign',
          data: { campaignName }
        });
        
        setIsBotReplying(false);
        if (isNewChat) {
          setIsCreatingFirstMessage(false);
        }
        return;
      }
      
      // --- AJOUT : gestion du clonage de campagnes ---
      if (messageAnalysis.type === 'campaign_clone') {
        console.log('Campaign clone intent detected:', messageAnalysis);
        // Extract campaign name from intent detection
        const campaignName = messageAnalysis.campaign_name;
        
        if (!campaignName) {
          const botResponseText = 'Je n\'ai pas compris le nom de la campagne. Pourriez-vous préciser quelle campagne vous souhaitez dupliquer ?';
          const botMessage = { from: 'bot', text: botResponseText, timestamp: new Date() };
          await createMessage(currentChat.id, 'bot', botResponseText);
          setMessages(prev => [...prev.filter(m => m.text !== '...'), botMessage]);
          setIsBotReplying(false);
          if (isNewChat) {
            setIsCreatingFirstMessage(false);
          }
          return;
        }
        
        // Ask the user for a new name for the cloned campaign
        const botResponseText = `Pour dupliquer la campagne "${campaignName}", veuillez me donner un nom pour la nouvelle campagne.`;
        const botMessage = { from: 'bot', text: botResponseText, timestamp: new Date() };
        await createMessage(currentChat.id, 'bot', botResponseText);
        setMessages(prev => [...prev.filter(m => m.text !== '...'), botMessage]);
        
        // Open the dialog to enter a new name
        handleOpenCloneDialog(campaignName);
        
        setIsBotReplying(false);
        if (isNewChat) {
          setIsCreatingFirstMessage(false);
        }
        return;
      }
      
      // --- AJOUT : gestion de la création de campagne ---
      if (messageAnalysis.type === 'campaign_create_percent') {
        // Utilise les entités extraites par l'IA
        const email_name = messageAnalysis.email_name;
        const percent = Number(messageAnalysis.percent);
        const segment_name = messageAnalysis.segment_name;
        console.log('DEBUG campaign_create_percent - valeurs extraites:', { email_name, percent, segment_name, messageAnalysis });
        if (!email_name || !segment_name || isNaN(percent) || percent <= 0) {
          const botResponseText = '❌ Erreur : informations manquantes ou invalides pour la création de la campagne de test (email, segment ou pourcentage).';
          console.log('ERREUR - Champs invalides ou manquants:', { email_name, percent, segment_name, messageAnalysis });
          setMessages(prev => [...prev.filter(m => m.text !== '...'), { from: 'bot', text: botResponseText, timestamp: new Date() }]);
          setIsBotReplying(false);
          if (isNewChat) {
            setIsCreatingFirstMessage(false);
          }
          return;
        }
        let botResponseText = 'Création de la campagne de test en cours...';
        setMessages(prev => [...prev.filter(m => m.text !== '...'), { from: 'bot', text: botResponseText, timestamp: new Date() }]);
        setIsBotReplying(true);
        try {
          console.log('APPEL API createTestCampaign avec:', { userMessageContent, percent, email_name, segment_name });
          const result = await createTestCampaign(userMessageContent, percent, email_name, segment_name);
          console.log('RÉPONSE API createTestCampaign:', result);
          if (result.success) {
            botResponseText = `✅ Campagne de test créée avec succès !`;
          } else {
            botResponseText = `❌ Erreur lors de la création de la campagne de test : ${result.message || 'Erreur inconnue.'}`;
            console.log('ERREUR - Backend a retourné une erreur:', result);
          }
        } catch (err) {
          botResponseText = `❌ Erreur technique lors de la création de la campagne de test : ${err.message}`;
          console.log('ERREUR TECHNIQUE lors de l\'appel à createTestCampaign:', err);
        }
        const botMessage = { from: 'bot', text: botResponseText, timestamp: new Date() };
        await createMessage(currentChat.id, 'bot', botResponseText);
        setMessages(prev => [...prev.filter(m => m.text !== 'Création de la campagne de test en cours...'), botMessage]);
        setIsBotReplying(false);
        if (isNewChat) {
          setIsCreatingFirstMessage(false);
        }
        return;
      }
      if (messageAnalysis.type === 'campaign_create') {
        // Récupère les credentials Mautic comme dans planification.jsx
        let botResponseText = 'Création de la campagne en cours...';
        setMessages(prev => [...prev.filter(m => m.text !== '...'), { from: 'bot', text: botResponseText, timestamp: new Date() }]);
        setIsBotReplying(true);
        try {
          // Appel API pour récupérer les credentials
          const credRes = await api.get('/auth/credentials');
          const creds = credRes.data?.credentials || {};
          if (!creds.mautic_url || !creds.mautic_username || !creds.mautic_password) {
            botResponseText = '❌ Erreur : Identifiants Mautic manquants. Veuillez configurer vos accès Mautic dans les préférences.';
            const botMessage = { from: 'bot', text: botResponseText, timestamp: new Date() };
            await createMessage(currentChat.id, 'bot', botResponseText);
            setMessages(prev => [...prev.filter(m => m.text !== 'Création de la campagne en cours...'), botMessage]);
            setIsBotReplying(false);
            return;
          }
          const mauticCredentials = {
            mautic_url: creds.mautic_url,
            mautic_username: creds.mautic_username,
            mautic_password: creds.mautic_password
          };
          const genResult = await generateCampaignFromPrompt(messageAnalysis.message, mauticCredentials);
          if (genResult.success && genResult.campaign_json) {
            const createResult = await createCampaignFromJson(genResult.campaign_json, mauticCredentials);
            if (createResult.success) {
              botResponseText = `✅ Campagne créée avec succès !`;
            } else {
              botResponseText = `❌ Erreur lors de la création de la campagne : ${createResult.message || 'Erreur inconnue.'}`;
            }
          } else {
            botResponseText = `❌ Erreur lors de la génération de la campagne : ${genResult.message || 'Erreur inconnue.'}`;
          }
        } catch (err) {
          botResponseText = `❌ Erreur technique lors de la création de la campagne : ${err.message}`;
        }
        const botMessage = { from: 'bot', text: botResponseText, timestamp: new Date() };
        await createMessage(currentChat.id, 'bot', botResponseText);
        setMessages(prev => [...prev.filter(m => m.text !== 'Création de la campagne en cours...'), botMessage]);
        setIsBotReplying(false);
        if (isNewChat) {
          setIsCreatingFirstMessage(false);
        }
        return;
      }
      
      // Log the original message for debugging
      console.log("Original user message:", userMessageContent);
      console.log("Intent detection result:", messageAnalysis);
      
      // Detect language based on user query for consistent error handling
      const isUserQueryFrench = userMessageContent.match(/[àáâäæçèéêëîïôœùûüÿ]/i) || 
                             /analyse|segment|campagne|affiche|montre|combien|nombre|quels|disponible/.test(userMessageContent.toLowerCase());
      console.log(`Language detection: ${isUserQueryFrench ? 'French' : 'English'}`);
      
      let aiResponse;
      
      // For general queries, first check the Knowledge Base
      if (messageAnalysis.type === 'general') {
        console.log("Checking Knowledge Base for answer...");
        try {
          const kbResponse = await queryKnowledgeBase(userMessageContent);
          // If Knowledge Base provided an answer, use it and RETURN immediately
          if (kbResponse.from_kb && kbResponse.response) {
            console.log("Found answer in Knowledge Base");
            const botMessage = {
              from: 'bot',
              text: kbResponse.response,
              timestamp: new Date(),
              kb_sources: kbResponse.sources || null
            };
            await createMessage(currentChat.id, 'bot', kbResponse.response);
            setMessages(prev => [...prev.filter(m => m.text !== '...'), botMessage]);
            setIsBotReplying(false);
            return;
          }
        } catch (kbError) {
          console.error("Error querying Knowledge Base:", kbError);
          
          // Check if this is a quota error from the Knowledge Base
          const kbErrorMessage = kbError.message || '';
          if (kbErrorMessage.includes("⚠️ Limite de quota API atteinte") || 
              kbErrorMessage.toLowerCase().includes('quota') ||
              kbErrorMessage.toLowerCase().includes('429') ||
              kbErrorMessage.toLowerCase().includes('rate limit')) {
            const quotaErrorText = "⚠️ Limite de quota API atteinte. Votre quota d'utilisation de l'API Google Gemini a été dépassé. Veuillez vérifier votre plan et vos détails de facturation, ou réessayer plus tard.";
            const botMessage = { 
              from: 'bot', 
              text: quotaErrorText, 
              timestamp: new Date()
            };
            await createMessage(currentChat.id, 'bot', quotaErrorText);
            setMessages(prev => [...prev.filter(m => m.text !== '...'), botMessage]);
            setIsBotReplying(false);
            return;
          }
          
          // Continue with regular flow if KB query fails with other errors
        }
      }
      
      // Handle simple greetings and generic messages specially
      if (messageAnalysis.type === 'general' && messageAnalysis.confidence < 0.3) {
        // Handle low-confidence general queries (which could be typos or random text)
        const unknownMessage = isUserQueryFrench 
          ? "Je ne comprends pas votre demande. Pourriez-vous reformuler ou préciser ce que vous recherchez ? Vous pouvez me demander d'analyser un segment, d'afficher les détails d'une campagne ou poser une question sur vos données."
          : "I don't understand your request. Could you rephrase or be more specific about what you're looking for? You can ask me to analyze a segment, show campaign details, or ask a question about your data.";
        aiResponse = {
          success: true,
          plain_text_analysis: unknownMessage,
          response: unknownMessage
        };
      } else if (messageAnalysis.type === 'campaign_comparison') {
        // Initialize botResponseText with a default value
        let botResponseText = "Processing your campaign comparison request...";
        
        try {
          const comparisonResult = await compareCampaigns(messageAnalysis.message);
          
          if (comparisonResult.success && comparisonResult.comparison_text) {
            botResponseText = comparisonResult.comparison_text;
          } else {
            // Handle error case
            botResponseText = comparisonResult.response || "I couldn't compare the campaigns. Please make sure you've specified two valid campaign names.";
            
            // If there are available campaigns, list them
            if (comparisonResult.available_campaigns && comparisonResult.available_campaigns.length > 0) {
              const availableCampaigns = comparisonResult.available_campaigns.slice(0, 10);
              const campaignsList = availableCampaigns.map((campaign, index) => `${index + 1}. ${campaign}`).join('\n');
              botResponseText += `\n\nHere are some available campaigns:\n${campaignsList}`;
              if (comparisonResult.available_campaigns.length > 10) {
                botResponseText += `\n...and ${comparisonResult.available_campaigns.length - 10} more`;
              }
            }
          }
          
          // Store the response in aiResponse to use the standard flow
          aiResponse = {
            success: comparisonResult.success || false,
            plain_text_analysis: botResponseText,
            response: botResponseText
          };
        } catch (error) {
          console.error("Error in campaign comparison:", error);
          botResponseText = `Error comparing campaigns: ${error.message}`;
          aiResponse = {
            success: false,
            plain_text_analysis: botResponseText,
            response: botResponseText
          };
        }
      } else if (messageAnalysis.type === 'campaign') {
        aiResponse = await analyzeCampaignPerformance(messageAnalysis.message);
      } else if (messageAnalysis.type === 'segment_display') {
        // Handle display of a specific segment
        console.log(`Displaying segment details for: ${messageAnalysis.segment_name || 'unknown segment'}`);
        
        // Use a lighter approach for segment display - first try to get segments list
        try {
          const segmentsResponse = await sendQueryToAI(isUserQueryFrench ? "liste tous les segments" : "list all segments");
          
          // Check if we have the segment in the list
          if (segmentsResponse.segments && segmentsResponse.segments.length > 0) {
            const segments = segmentsResponse.segments;
            const segmentName = messageAnalysis.detected_entity?.toLowerCase();
            
            // Try to find the requested segment by name (case-insensitive)
            const matchedSegment = segments.find(s => 
              s.name.toLowerCase() === segmentName || 
              s.id.toLowerCase() === segmentName || 
              s.name.toLowerCase().includes(segmentName));
            
                         if (matchedSegment) {
               // Found the segment - format a simple display response without running full analysis
               console.log(`Found segment for display: ${matchedSegment.name}`);
               
               try {
                 // Get profile count by doing a minimal segment query
                 // This is much more efficient than running a full analysis
                 console.log(`Getting profile count for segment: ${matchedSegment.id}`);
                 
                 // We'll use a focused call to get just the profile count
                 // This will run a much lighter query than full segment analysis
                 const countResponse = await analyzeSegment(`count profiles in segment ${matchedSegment.name}`);
                 
                 // Extract the profile count from the response
                 let profileCount = "N/A";
                 if (countResponse && countResponse.profile_summary && 
                     typeof countResponse.profile_summary.total_count !== 'undefined') {
                   profileCount = countResponse.profile_summary.total_count;
                   console.log(`Found ${profileCount} profiles for segment ${matchedSegment.name}`);
                 } else {
                   console.log(`Could not retrieve profile count for segment ${matchedSegment.name}`);
                 }
                 
                 // Format user-friendly segment details in the detected language
                 if (isUserQueryFrench) {
                   aiResponse = {
                     success: true,
                     segment_name: matchedSegment.name,
                     segment_id: matchedSegment.id,
                     plain_text_analysis: `Détails du Segment:
  
Nom: ${matchedSegment.name}
ID: ${matchedSegment.id}
Description: ${matchedSegment.description || 'Aucune description disponible'}
Portée: ${matchedSegment.scope || 'Non spécifié'}
Statut: ${matchedSegment.enabled ? 'Activé' : 'Désactivé'}
Tags: ${matchedSegment.tags && matchedSegment.tags.length > 0 ? matchedSegment.tags.join(', ') : 'Aucun'}
Lecture seule: ${matchedSegment.readOnly ? 'Oui' : 'Non'}
Caché: ${matchedSegment.hidden ? 'Oui' : 'Non'}
Profils: ${profileCount}`,
                     profile_summary: countResponse?.profile_summary || { total_count: profileCount }
                   };
                 } else {
                   aiResponse = {
                     success: true,
                     segment_name: matchedSegment.name,
                     segment_id: matchedSegment.id, 
                     plain_text_analysis: `Segment Details:
  
Name: ${matchedSegment.name}
ID: ${matchedSegment.id}
Description: ${matchedSegment.description || 'No description available'}
Scope: ${matchedSegment.scope || 'Not specified'}
Status: ${matchedSegment.enabled ? 'Enabled' : 'Disabled'}
Tags: ${matchedSegment.tags && matchedSegment.tags.length > 0 ? matchedSegment.tags.join(', ') : 'None'}
Read only: ${matchedSegment.readOnly ? 'Yes' : 'No'}
Hidden: ${matchedSegment.hidden ? 'Yes' : 'No'}
Profiles: ${profileCount}`,
                     profile_summary: countResponse?.profile_summary || { total_count: profileCount }
                   };
                 }
               } catch (countError) {
                 console.error(`Error getting profile count for segment ${matchedSegment.name}:`, countError);
                 
                 // Even if count fails, still show segment details without count
                 const profileCount = "N/A";
                 
                 // Format user-friendly segment details in the detected language
                 if (isUserQueryFrench) {
                   aiResponse = {
                     success: true,
                     segment_name: matchedSegment.name,
                     segment_id: matchedSegment.id,
                     plain_text_analysis: `Détails du Segment:
  
Nom: ${matchedSegment.name}
ID: ${matchedSegment.id}
Description: ${matchedSegment.description || 'Aucune description disponible'}
Portée: ${matchedSegment.scope || 'Non spécifié'}
Statut: ${matchedSegment.enabled ? 'Activé' : 'Désactivé'}
Tags: ${matchedSegment.tags && matchedSegment.tags.length > 0 ? matchedSegment.tags.join(', ') : 'Aucun'}
Lecture seule: ${matchedSegment.readOnly ? 'Oui' : 'Non'}
Caché: ${matchedSegment.hidden ? 'Oui' : 'Non'}
Profils: ${profileCount}`
                   };
                 } else {
                   aiResponse = {
                     success: true,
                     segment_name: matchedSegment.name,
                     segment_id: matchedSegment.id, 
                     plain_text_analysis: `Segment Details:
  
Name: ${matchedSegment.name}
ID: ${matchedSegment.id}
Description: ${matchedSegment.description || 'No description available'}
Scope: ${matchedSegment.scope || 'Not specified'}
Status: ${matchedSegment.enabled ? 'Enabled' : 'Disabled'}
Tags: ${matchedSegment.tags && matchedSegment.tags.length > 0 ? matchedSegment.tags.join(', ') : 'None'}
Read only: ${matchedSegment.readOnly ? 'Yes' : 'No'}
Hidden: ${matchedSegment.hidden ? 'Yes' : 'No'}
Profiles: ${profileCount}`
                   };
                 }
               }
            } else {
              // Segment not found in list, use analyzeSegment as fallback to try more advanced matching
              console.log(`Segment not found in list, trying analyzeSegment as fallback`);
        aiResponse = await analyzeSegment(messageAnalysis.message);
              
              // If fallback also fails, format the error with available segments list
              if (aiResponse && !aiResponse.success && (aiResponse.message || "").includes("Could not identify a specific segment")) {
                const availableSegmentsText = isUserQueryFrench 
                  ? "Voici les segments disponibles:" 
                  : "Here are the available segments:";
                const segmentsList = segments.slice(0, 10).map((segment, index) => 
                  `${index + 1}. ${segment.name} (${segment.id})`).join('\n');
                
                if (isUserQueryFrench) {
                  aiResponse.plain_text_analysis = `Je n'ai pas pu trouver le segment demandé. Impossible d'identifier un nom de segment spécifique dans votre requête: '${messageAnalysis.message}'.\n\n${availableSegmentsText}\n${segmentsList}`;
                } else {
                  aiResponse.plain_text_analysis = `I couldn't find the requested segment. Could not identify a specific segment name from your query: '${messageAnalysis.message}'.\n\n${availableSegmentsText}\n${segmentsList}`;
                }
              }
            }
          } else {
            // No segments in list, fallback to analyzeSegment
            console.log(`No segments in list, using analyzeSegment as fallback`);
            aiResponse = await analyzeSegment(messageAnalysis.message);
          }
        } catch (error) {
          console.error("Error in segment display flow:", error);
          // Fallback to original approach if something went wrong
          aiResponse = await analyzeSegment(messageAnalysis.message);
          
          // If segment wasn't found, format the error in the right language
          if (aiResponse && !aiResponse.success && (aiResponse.message || "").includes("Could not identify a specific segment")) {
            if (isUserQueryFrench) {
              aiResponse.plain_text_analysis = `Je n'ai pas pu trouver le segment demandé. Impossible d'identifier un nom de segment spécifique dans votre requête: '${messageAnalysis.message}'.`;
            } else {
              aiResponse.plain_text_analysis = `I couldn't find the requested segment. Could not identify a specific segment name from your query: '${messageAnalysis.message}'.`;
            }
          }
        }
      } else if (messageAnalysis.type === 'segment_list') {
        // Handle list-all segments request
        console.log("Requesting all available segments");
        aiResponse = await sendQueryToAI(isUserQueryFrench ? "liste tous les segments" : "list all segments");
      } else if (messageAnalysis.type === 'segment_analysis') {
        // Handle segment analysis
        console.log(`Analyzing segment: ${messageAnalysis.segment_name || 'unspecified'}`);
        aiResponse = await analyzeSegment(messageAnalysis.message);
        
        // If segment wasn't found, format the error in the right language and add available segments
        if (aiResponse && !aiResponse.success && (aiResponse.message || "").includes("Could not identify a specific segment")) {
          // Try to get list of segments
          try {
            const segmentsResponse = await sendQueryToAI(isUserQueryFrench ? "liste tous les segments" : "list all segments");
            if (segmentsResponse.segments && segmentsResponse.segments.length > 0) {
              const segments = segmentsResponse.segments;
              const availableSegmentsText = isUserQueryFrench 
                ? "Voici les segments disponibles:" 
                : "Here are the available segments:";
              const noDescText = isUserQueryFrench ? "Aucune description" : "No description";
              const segmentsList = segments.slice(0, 10).map((segment, index) => 
                `${index + 1}. ${segment.name} (${segment.id})`).join('\n');
              
              if (isUserQueryFrench) {
                aiResponse.plain_text_analysis = `Je n'ai pas pu analyser les segments. Impossible d'identifier un nom de segment spécifique dans votre requête: '${messageAnalysis.message}'. Veuillez préciser le segment à analyser.\n\n${availableSegmentsText}\n${segmentsList}`;
              } else {
                aiResponse.plain_text_analysis = `I couldn't analyze the segments. Could not identify a specific segment name from your query: '${messageAnalysis.message}'. Please specify the segment you want to analyze.\n\n${availableSegmentsText}\n${segmentsList}`;
              }
            }
          } catch (listError) {
            console.error("Error fetching segments list:", listError);
          }
        }
      } else if (messageAnalysis.type === 'segment_count') {
        // Handle segment count request
        console.log(`Getting count for segment: ${messageAnalysis.segment_name || 'unspecified'}`);
        // We'll use analyzeSegment which already includes the count information
        aiResponse = await analyzeSegment(messageAnalysis.message);
        
        // If segment wasn't found, format the error in the right language
        if (aiResponse && !aiResponse.success && (aiResponse.message || "").includes("Could not identify a specific segment")) {
          if (isUserQueryFrench) {
            aiResponse.message = `Je n'ai pas pu compter les profils. Impossible d'identifier un segment spécifique dans votre requête: '${messageAnalysis.message}'. Veuillez préciser le segment.`;
      } else {
            aiResponse.message = `I couldn't count the profiles. Could not identify a specific segment in your query: '${messageAnalysis.message}'. Please specify the segment.`;
          }
        }
      } else if (messageAnalysis.type === 'general') {
        // For general messages, check if they contain any segment/campaign related keywords
        const hasRelevantKeywords = /segment|campaign|campagne|profile|profil|user|utilisateur|client|customer|data|donnée/i.test(messageAnalysis.message);
        
        if (hasRelevantKeywords) {
          // Only send to AI if it's likely related to our domain
          console.log("General query with relevant keywords, sending to AI");
          aiResponse = await sendQueryToAI(messageAnalysis.message);
        } else {
          // For truly generic messages, provide helpful guidance rather than querying the AI
          console.log("Truly generic message, not sending to AI");
          const genericResponse = isUserQueryFrench 
            ? "Je suis conçu pour répondre à des questions sur les segments, les campagnes et les données client. Pourriez-vous me poser une question plus spécifique dans ce domaine?"
            : "Je n'ai pas compris votre question. Veuillez fournir des détails plus précis";
            
          aiResponse = {
            success: true,
            plain_text_analysis: genericResponse,
            response: genericResponse
          };
        }
      } else {
        // For other types of intents, proceed with the query
        aiResponse = await sendQueryToAI(messageAnalysis.message);
      }
  
      // Check if the aiResponse contains a quota error in any field
      if (aiResponse) {
        const checkFields = [
          aiResponse.message, 
          aiResponse.response, 
          aiResponse.plain_text_analysis,
          aiResponse.campaign_analysis_text,
          aiResponse.performance_report
        ];
        
        let hasQuotaError = false;
        for (const field of checkFields) {
          if (field && 
              (field.includes("⚠️ Limite de quota API atteinte") ||
               field.toLowerCase().includes('quota exceeded') ||
               (field.toLowerCase().includes('quota') && field.includes('429')) ||
               field.toLowerCase().includes('rate limit') ||
               field.toLowerCase().includes('billing details'))) {
            hasQuotaError = true;
            break;
          }
        }
        
        if (hasQuotaError) {
          const quotaErrorText = "⚠️ Limite de quota API atteinte. Votre quota d'utilisation de l'API Google Gemini a été dépassé. Veuillez vérifier votre plan et vos détails de facturation, ou réessayer plus tard.";
          const quotaBotMessage = { 
            from: 'bot', 
            text: quotaErrorText, 
            timestamp: new Date(),
            kb_sources: null
          };
          await createMessage(currentChat.id, 'bot', quotaErrorText);
          setMessages(prev => [...prev.filter(m => m.text !== '...'), quotaBotMessage]);
          setIsBotReplying(false);
          if (isNewChat) {
            setIsCreatingFirstMessage(false);
          }
          return;
        }
      }
      
      // Set a default response, but this will likely be overridden by the actual response from the API
      let botResponseText = "I'm not sure I understand your request. Could you reformulate or provide more details about what you're looking for?";
      
      // Handle different response types
      if (messageAnalysis.type === 'campaign_comparison') {
        // We've already set botResponseText in the campaign_comparison section
        botResponseText = aiResponse.plain_text_analysis || aiResponse.response || botResponseText;
      } else if (aiResponse.plain_text_analysis) {
        // For segment_display, we don't want the full analysis text, just the details
        if (messageAnalysis.type === 'segment_display' && aiResponse.profile_summary) {
            const segmentName = aiResponse.segment_name || messageAnalysis.segment_name || 'this segment';
            const count = aiResponse.profile_summary.total_count;
            const segment = {
                name: aiResponse.segment_name,
                id: aiResponse.segment_id,
                description: aiResponse.profile_summary?.description || '', 
                scope: aiResponse.profile_summary?.scope || '',
                enabled: aiResponse.profile_summary?.enabled || true,
                tags: aiResponse.profile_summary?.tags || [],
                readOnly: aiResponse.profile_summary?.readOnly || false,
                hidden: aiResponse.profile_summary?.hidden || false,
                profile_count: count
            };
            
            // Use API response directly, don't use hardcoded text
            botResponseText = aiResponse.plain_text_analysis;
        } else {
            // Use the plain text analysis directly from the API
             botResponseText = aiResponse.plain_text_analysis;
        }
      } else if (aiResponse.campaign_analysis_text || aiResponse.performance_report) {
        // Use the campaign analysis directly from the API
        botResponseText = aiResponse.campaign_analysis_text || aiResponse.performance_report;
      } else if (aiResponse.segments && aiResponse.segments.length > 0) {
        // Use the segments data but let any text formatting come from the API response
        if (aiResponse.formatted_response) {
          botResponseText = aiResponse.formatted_response;
        } else {
          // If no formatted response is available, do minimal formatting
        const segments = aiResponse.segments;
          const isUserQueryFrench = userMessageContent.match(/[àáâäæçèéêëîïôœùûüÿ]/i) || 
                                   /voici|segments|disponible|tous|les|affiche|montre|liste/.test(userMessageContent.toLowerCase());
          
        if (segments.length === 1) {
          const segment = segments[0];
            const formatTitle = isUserQueryFrench ? "Détails du Segment:" : "Segment Details:";
            const nameLabel = isUserQueryFrench ? "Nom" : "Name";
            const descLabel = isUserQueryFrench ? "Description" : "Description";
            const scopeLabel = isUserQueryFrench ? "Portée" : "Scope";
            const statusLabel = isUserQueryFrench ? "Statut" : "Status";
            const tagsLabel = isUserQueryFrench ? "Tags" : "Tags"; 
            const readOnlyLabel = isUserQueryFrench ? "Lecture seule" : "Read Only";
            const hiddenLabel = isUserQueryFrench ? "Caché" : "Hidden";
            const profilesLabel = isUserQueryFrench ? "Profils" : "Profiles";
            
            const noDesc = isUserQueryFrench ? "Aucune description disponible" : "No description available";
            const notSpecified = isUserQueryFrench ? "Non spécifié" : "Not specified";
            const enabled = isUserQueryFrench ? "Activé" : "Enabled";
            const disabled = isUserQueryFrench ? "Désactivé" : "Disabled";
            const yes = isUserQueryFrench ? "Oui" : "Yes";
            const no = isUserQueryFrench ? "Non" : "No";
            const none = isUserQueryFrench ? "Aucun" : "None";
            
            botResponseText = `${formatTitle}
  
  ${nameLabel}: ${segment.name}
  ID: ${segment.id}
  ${descLabel}: ${segment.description || noDesc}
  ${scopeLabel}: ${segment.scope || notSpecified}
  ${statusLabel}: ${segment.enabled ? enabled : disabled}
  ${tagsLabel}: ${segment.tags && segment.tags.length > 0 ? segment.tags.join(', ') : none}
  ${readOnlyLabel}: ${segment.readOnly ? yes : no}
  ${hiddenLabel}: ${segment.hidden ? yes : no}
  ${profilesLabel}: ${segment.profile_count !== undefined ? segment.profile_count : 'N/A'}`;
        } else {
            // Customize introduction based on language detection
            const availableText = isUserQueryFrench ? "disponibles" : "available";
            const foundText = isUserQueryFrench ? "Trouvé" : "Found";
            const segmentsText = isUserQueryFrench ? "segments" : "segments";
            const noDescText = isUserQueryFrench ? "Aucune description" : "No description";
            
          const introduction = messageAnalysis.type === 'segment_list' 
              ? `${isUserQueryFrench ? 'Voici les' : 'Here are the'} ${segments.length} ${segmentsText} ${availableText}:\n\n` 
              : `${foundText} ${segments.length} ${segmentsText}:\n\n`;
            
          botResponseText = introduction + 
              segments.map((segment, index) => `${index + 1}. ${segment.name} (${segment.id})\n   ${segment.description || noDescText}`).join('\n\n');
          }
        }
      } else if (aiResponse.response) {
        botResponseText = aiResponse.response;
      } else if (aiResponse.message) {
        // Handle error messages or other messages
        botResponseText = aiResponse.message;
      } else if (typeof aiResponse === 'string') {
        botResponseText = aiResponse;
      }
      
      // Special handling for segment_count intent
      if (messageAnalysis.type === 'segment_count') {
        // Check if we have profile summary information from the segment analysis
        if (aiResponse.profile_summary && typeof aiResponse.profile_summary.total_count !== 'undefined') {
          const segmentName = aiResponse.segment_name || messageAnalysis.segment_name || 'ce segment';
          const count = aiResponse.profile_summary.total_count;
          
          // Detect language based on the user's query
          const isUserQueryFrench = userMessageContent.match(/[àáâäæçèéêëîïôœùûüÿ]/i) || 
                                   /combien|nombre|profils|dans|segment|utilisateurs|contacts/.test(userMessageContent.toLowerCase());
          
          // Create a simple response with just the count information
          if (isUserQueryFrench) {
            botResponseText = `Le segment "${segmentName}" contient ${count} profils.`;
          } else {
            botResponseText = `The segment "${segmentName}" contains ${count} profiles.`;
          }
        }
      }
      
      // Special handling for segment_list when no segments were returned
      if (messageAnalysis.type === 'segment_list' && (!aiResponse.segments || aiResponse.segments.length === 0)) {
        // Try again with a more direct query
        try {
          // Detect language based on the user's query
          const isUserQueryFrench = userMessageContent.match(/[àáâäæçèéêëîïôœùûüÿ]/i) || 
                                   /voici|segments|disponible|tous|les|affiche|montre|liste/.test(userMessageContent.toLowerCase());
                                   
          const directResponse = await sendQueryToAI(isUserQueryFrench ? "liste tous les segments" : "list all segments");
          if (directResponse.segments && directResponse.segments.length > 0) {
            const segments = directResponse.segments;
            const availableText = isUserQueryFrench ? "disponibles" : "available";
            const segmentsText = isUserQueryFrench ? "segments" : "segments";
            const noDescText = isUserQueryFrench ? "Aucune description" : "No description";
            
            botResponseText = `${isUserQueryFrench ? 'Voici les' : 'Here are the'} ${segments.length} ${segmentsText} ${availableText}:\n\n` + 
              segments.map((segment, index) => `${index + 1}. ${segment.name} (${segment.id})\n   ${segment.description || noDescText}`).join('\n\n');
          } else {
            // No segments found, provide appropriate language response
            botResponseText = isUserQueryFrench 
              ? "Je n'ai pas pu trouver de segments disponibles. Veuillez vérifier votre système de gestion des segments."
              : "I couldn't find any available segments. Please check your segment management system.";
          }
        } catch (directError) {
          console.error("Error with direct API call:", directError);
          // Handle error in appropriate language
          const isUserQueryFrench = userMessageContent.match(/[àáâäæçèéêëîïôœùûüÿ]/i) || 
                                   /voici|segments|disponible|tous|les|affiche|montre|liste/.test(userMessageContent.toLowerCase());
          botResponseText = isUserQueryFrench
            ? "Je n'ai pas pu récupérer la liste des segments en raison d'une erreur technique. Veuillez réessayer plus tard."
            : "I couldn't retrieve the segment list due to a technical error. Please try again later.";
        }
      }
  
      // Replace typing indicator with actual response
      const botMessage = { 
        from: 'bot', 
        text: botResponseText, 
        timestamp: new Date(),
        // Add KB sources if available
        kb_sources: aiResponse.kb_sources || aiResponse.sources || null
      };
      await createMessage(currentChat.id, 'bot', botResponseText);
      setMessages(prev => [...prev.filter(m => m.text !== '...'), botMessage]);
  
      } catch (error) {
      console.error('Error getting AI response:', error);
      
      // Detect language based on the user's query for error message
      const isUserQueryFrench = userMessageContent.match(/[àáâäæçèéêëîïôœùûüÿ]/i) || 
                               /analyse|segment|campagne|affiche|montre|combien|nombre|quels|disponible/.test(userMessageContent.toLowerCase());
      
      // Check if this is a quota error first
      const mainErrorMessage = error.message || '';
      if (mainErrorMessage.includes("⚠️ Limite de quota API atteinte") || 
          mainErrorMessage.toLowerCase().includes('quota') ||
          mainErrorMessage.toLowerCase().includes('429') ||
          mainErrorMessage.toLowerCase().includes('rate limit') ||
          mainErrorMessage.toLowerCase().includes('billing details') ||
          mainErrorMessage.toLowerCase().includes('exceeded')) {
        const quotaErrorText = "⚠️ Limite de quota API atteinte. Votre quota d'utilisation de l'API Google Gemini a été dépassé. Veuillez vérifier votre plan et vos détails de facturation, ou réessayer plus tard.";
        const errorBotMessage = { 
          from: 'bot', 
          text: quotaErrorText, 
          timestamp: new Date(),
          kb_sources: null
        };
        await createMessage(currentChat.id, 'bot', quotaErrorText);
        setMessages(prev => [...prev.filter(m => m.text !== '...'), errorBotMessage]);
        setIsBotReplying(false);
        if (isNewChat) {
          setIsCreatingFirstMessage(false);
        }
        return;
      }
      
      // Format error message in the appropriate language for other errors
      let errorText;
      if (isUserQueryFrench) {
        errorText = `Erreur: ${error.message}`;
        
        // French common errors handling
        if (error.message.includes("Could not identify")) {
          errorText = "Je n'ai pas pu identifier le segment ou la campagne spécifique. Veuillez préciser votre demande.";
        } else if (error.message.includes("timeout") || error.message.includes("timed out")) {
          errorText = "La requête a pris trop de temps. Veuillez réessayer avec une demande plus simple.";
        } else if (error.message.includes("network") || error.message.includes("connection")) {
          errorText = "Problème de connexion au serveur. Veuillez vérifier votre connexion et réessayer.";
        }
      } else {
        errorText = `Error: ${error.message}`;
        
        // English common errors handling
        if (error.message.includes("Could not identify")) {
          errorText = "I couldn't identify the specific segment or campaign. Please clarify your request.";
        } else if (error.message.includes("timeout") || error.message.includes("timed out")) {
          errorText = "The request took too long. Please try again with a simpler query.";
        } else if (error.message.includes("network") || error.message.includes("connection")) {
          errorText = "Problem connecting to the server. Please check your connection and try again.";
        }
      }
      
      const errorMessage = { 
        from: 'bot', 
        text: errorText, 
        timestamp: new Date(),
        kb_sources: null
      };
      await createMessage(currentChat.id, 'bot', errorText);
      setMessages(prev => [...prev.filter(m => m.text !== '...'), errorMessage]);
      } finally {
        setIsBotReplying(false);
        // Reset the flag after message handling is complete
        if (isNewChat) {
          setIsCreatingFirstMessage(false);
        }
      }
  }, [userInput, activeChat, chats, isBotReplying, detectMessageIntent, isCreatingFirstMessage]);
  
  // Add window resize listener to automatically close sidebar on small screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1200 && isSidebarOpen) {
        setIsSidebarOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isSidebarOpen]);

  // Tour steps configuration
  const tourSteps = [
    {
      target: '[data-tourid="conversations-sidebar"]',
      content: 'Voici votre liste de conversations. Vous pouvez rechercher, créer de nouvelles conversations ou gérer vos conversations existantes.',
      placement: 'right',
      disableBeacon: true,
    },
    {
      target: '[data-tourid="search-conversations"]',
      content: 'Utilisez cette barre de recherche pour trouver rapidement une conversation spécifique.',
      placement: 'bottom',
    },
    {
      target: '[data-tourid="new-chat-button"]',
      content: 'Cliquez ici pour créer une nouvelle conversation.Vous pouvez renommer et supprimer la conversation en cliquant sur les trois points.',
      placement: 'bottom',
    },
    {
      target: '[data-tourid="chat-messages-area"]',
      content: 'C\'est ici que s\'affichent vos messages et les réponses de l\'IA. Vous pouvez poser des questions sur vos segments, campagnes et données.',
      placement: 'top',
    },
    {
      target: '[data-tourid="chat-input"]',
      content: 'Tapez votre question ici. Vous pouvez demander à l\'IA d\'analyser un segment, créer une campagne, comparer des performances, etc.',
      placement: 'top',
    },
    {
      target: '[data-tourid="send-button"]',
      content: 'Cliquez sur ce bouton ou appuyez sur Entrée pour envoyer votre message.',
      placement: 'top',
    },
    {
      target: '[data-tourid="actions-sidebar"]',
      content: 'Ce panneau contient toutes les actions rapides disponibles sur le chat. Cliquez sur une action pour pré-remplir le champ de saisie avec un exemple de commande.',
      placement: 'left',
    },
    {
      target: '[data-tourid="sidebar-toggle"]',
      content: 'Utilisez ce bouton pour afficher ou masquer le panneau d\'actions rapides.',
      placement: 'left',
    },
  ];

  // Handle tour callbacks
  const handleTourCallback = useCallback((data) => {
    const { status, type, index } = data;
    
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRunTour(false);
      setTourStepIndex(0);
      // Save to localStorage that user has completed the tour
      localStorage.setItem('chat-tour-completed', 'true');
    } else if (type === EVENTS.STEP_BEFORE) {
      // Open the actions sidebar before showing the actions sidebar step (index 6) or sidebar toggle (index 7)
      if ((index === 6 || index === 7) && !isSidebarOpen) {
        setIsSidebarOpen(true);
      }
    } else if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      setTourStepIndex(index + (type === EVENTS.STEP_AFTER ? 1 : 0));
    }
  }, [isSidebarOpen]);

  // Check if user has completed tour before, and offer to restart
  useEffect(() => {
    const tourCompleted = localStorage.getItem('chat-tour-completed');
    // Auto-start tour for first-time users
    if (!tourCompleted) {
      setTimeout(() => setRunTour(true), 1000);
    }
  }, []);

  // Chat is now accessible to all users with quota-based limits
  // No module check needed - quota enforcement happens when sending messages
  
  return (
    <Box sx={{
      display: 'flex',
      height: 'calc(100vh - 64px)',
      bgcolor: '#f9f9f9',
      position: 'relative',
      overflow: 'hidden' // Prevent scrollbars caused by absolute positioning
    }}>
      <GlobalStyles styles={typingAnimation} />
      
      {/* Joyride Tour Component */}
      <Joyride
        steps={tourSteps}
        run={runTour}
        stepIndex={tourStepIndex}
        continuous={true}
        showProgress={true}
        showSkipButton={true}
        callback={handleTourCallback}
        styles={{
          options: {
            primaryColor: '#1976d2',
            zIndex: 10000,
          },
          tooltip: {
            borderRadius: 8,
            fontFamily: 'Montserrat, Arial, sans-serif',
          },
          buttonNext: {
            backgroundColor: '#1976d2',
            fontSize: 14,
            padding: '8px 16px',
            borderRadius: 4,
            fontFamily: 'Montserrat, Arial, sans-serif',
          },
          buttonBack: {
            color: '#1976d2',
            fontSize: 14,
            fontFamily: 'Montserrat, Arial, sans-serif',
          },
          buttonSkip: {
            color: '#666',
            fontSize: 14,
            fontFamily: 'Montserrat, Arial, sans-serif',
          },
        }}
        locale={{
          back: 'Précédent',
          close: 'Fermer',
          last: 'Terminer',
          next: 'Suivant',
          skip: 'Passer',
        }}
      />
      
      {/* Colonne de gauche - Liste des conversations */}
      <Box 
        data-tourid="conversations-sidebar"
        sx={{ 
          width: { xs: '100%', md: '280px' },
          minWidth: { xs: '100%', md: '280px' },
          maxWidth: { xs: '100%', md: '280px' },
          display: { xs: isSidebarOpen ? 'none' : 'flex', md: 'flex' },
          flexDirection: 'column',
          bgcolor: '#ffffff',
          borderRight: '1px solid #e0e0e0',
          p: 1.5,
          overflowY: 'auto'
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ p: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>Conversations</Typography>
          <Tooltip title="Guide de démarrage">
            <IconButton 
              size="small" 
              onClick={() => {
                setRunTour(true);
                setTourStepIndex(0);
              }}
              sx={{ 
                color: '#1976d2',
                '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' }
              }}
            >
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Box display="flex" alignItems="center" gap={1} mb={1.5} px={1}>
          <Paper 
            data-tourid="search-conversations"
            elevation={0} 
            sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, borderRadius: 2, background: '#f1f3f4', flex: 1 }}
          >
            <SearchIcon sx={{ color: '#868e96', fontSize: 22, mr: 1 }} />
            <InputBase
              placeholder="Rechercher…"
              value={search}
              onChange={handleSearchChange}
              sx={{ fontFamily: 'Montserrat, Arial, sans-serif', fontSize: 15, flex: 1 }}
            />
          </Paper>
          <Tooltip title="Nouvelle conversation">
            <IconButton 
              data-tourid="new-chat-button"
              color="primary" 
              onClick={handleNewChat}
            >
              <AddCircleIcon sx={{ fontSize: 28 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
          {isLoadingChats ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List disablePadding>
              {filteredChats.map((chat) => (
                <ChatListItem
                  key={chat.id}
                  chat={chat}
                  isActive={chat.id === activeChat?.id}
                  onSelect={handleSelectChat}
                  onRename={handleInlineRename}
                  onDelete={handleDeleteChat}
                />
              ))}
            </List>
          )}
        </Box>
      </Box>

      {/* Section principale du chat */}
      <Box sx={{ 
        flexGrow: 1, 
        display: 'flex', 
        flexDirection: 'column',
        p: { xs: 1, sm: 2 },
        width: { 
          xs: '100%', 
          md: isSidebarOpen ? 'calc(100% - 280px - 300px)' : 'calc(100% - 280px)' // Account for left panel and right sidebar
        },
      }}>
        <Paper elevation={2} sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          borderRadius: 3, 
          overflow: 'hidden',
          maxHeight: 'calc(100vh - 80px)' // Prevent overflow
        }}>
          <Box 
            data-tourid="chat-messages-area"
            sx={{ flexGrow: 1, p: 2, overflowY: 'auto', bgcolor: '#f8f9fa' }}
          >
            {!activeChat ? (
              // Si aucune conversation n'est active, afficher le message de bienvenue.
              <Box textAlign="center" p={4}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#333' }}>
                  Bienvenue, {currentUser?.first_name || 'Utilisateur'} !
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  En quoi puis-je vous aider dans vos efforts marketing ?
                </Typography>
              </Box>
            ) : 
            // Si une conversation est active, afficher un loader ou les messages.
            isLoadingMessages ? (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={24} />
              </Box>
            ) : messages.length === 0 ? (
              <Box textAlign="center" p={4}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#333' }}>
                  Bienvenue, {currentUser?.first_name || currentUser?.username || 'Utilisateur'} !
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  En quoi puis-je vous aider dans vos efforts marketing ?
                </Typography>
              </Box>
            ) : (
              <Stack spacing={2} py={1}>
                {messages.map((msg, idx) => (
                  <MessageBubble key={`${msg.timestamp}-${idx}`} message={msg} index={idx} />
                ))}
                <div ref={chatEndRef} />
              </Stack>
            )}
          </Box>
          <Divider />
          <Box display="flex" alignItems="center" gap={1} p={1.5} bgcolor="#fff">
            <InputBase
              data-tourid="chat-input"
              inputRef={chatInputRef}
              placeholder={activeChat 
                ? "Posez votre question ici… / Ask your question here..." 
                : "Posez une question pour commencer une conversation… / Ask a question to start a conversation..."}
              fullWidth
              multiline
              maxRows={3} // Reduced max rows to prevent too large input area
              value={userInput}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              disabled={isBotReplying || isLoadingChats}
              sx={{ background: '#f1f3f4', borderRadius: 2, px: 2, py: 1, fontSize: 15, border: '1px solid #e0e0e0' }}
              aria-label="Chat input field"
            />
            <Tooltip title="Envoyer le message">
              <span>
                <IconButton 
                  data-tourid="send-button"
                  onClick={handleSendMessage}
                  disabled={isBotReplying || isLoadingChats}
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'white', // flèche blanche
                    width: 40,
                    height: 40,
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  }}
                >
                  <ArrowUpwardIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Paper>
      </Box>

      {/* Bouton pour ouvrir/fermer le volet latéral */}
      <Tooltip title={isSidebarOpen ? "Masquer le volet" : "Afficher le volet"}>
        <IconButton
          data-tourid="sidebar-toggle"
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          sx={{
            display: { xs: 'none', md: 'flex' }, // Show on medium screens and up
            position: 'absolute',
            top: '50%',
            right: isSidebarOpen ? '300px' : 0,
            transform: 'translateY(-50%)',
            bgcolor: 'white',
            border: '1px solid #e0e0e0',
            zIndex: 1300,
            transition: 'right 0.3s ease-in-out',
            '&:hover': { bgcolor: '#f0f0f0' }
          }}
        >
          {isSidebarOpen ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Tooltip>

      {/* Volet latéral (Insights & Knowledge Base) */}
      <Box 
        data-tourid="actions-sidebar"
        sx={{
          display: isSidebarOpen ? 'flex' : 'none',
          flexDirection: 'column',
          bgcolor: '#f5f7fa',
          borderLeft: '1px solid #e0e0e0',
          width: { xs: '100%', md: '300px' },
          minWidth: { xs: '100%', md: '300px' },
          maxWidth: { xs: '100%', md: '300px' },
          p: 2,
          overflowY: 'auto',
          position: { xs: 'absolute', md: 'relative' },
          right: { xs: 0, md: 'auto' },
          top: { xs: 0, md: 'auto' },
          height: { xs: '100%', md: 'auto' },
          zIndex: { xs: 1200, md: 'auto' }
        }}
      >
        {/* Actions rapides */}
        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 3, background: '#fff', boxShadow: '0 2px 12px 0 rgba(25, 118, 210, 0.06)' }}>
          <Typography variant="subtitle1" sx={{ color: '#1976d2', fontWeight: 700, mb: 2, fontFamily: 'Montserrat, Arial, sans-serif' }}>
            ACTIONS DISPONIBLES SUR LE CHAT QILINSA AMPLIFY
          </Typography>
          
          <List dense sx={{ p: 0 }}>
            {availableActions.map((action, index) => (
              <ListItemButton
                key={action.id}
                onClick={() => handleActionClick(action.prompt)}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  px: 1.5,
                  py: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  },
                  '&:focus': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    outline: '2px solid #1976d2',
                    outlineOffset: '-2px',
                  },
                  '&:focus-visible': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    outline: '2px solid #1976d2',
                    outlineOffset: '-2px',
                  },
                }}
                aria-label={action.label}
                tabIndex={0}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  <Box sx={{ 
                    color: '#1976d2', 
                    display: 'flex', 
                    alignItems: 'center',
                    flexShrink: 0
                  }}>
                    {action.icon}
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontFamily: 'Montserrat, Arial, sans-serif',
                      fontSize: 13,
                      fontWeight: 500,
                      color: '#212529',
                      flex: 1,
                      textAlign: 'left'
                    }}
                  >
                    {index + 1}- {action.label}
                  </Typography>
                </Box>
              </ListItemButton>
            ))}
          </List>
        </Paper>
      </Box>
      
      {/* Dialog de clonage de campagne */}
      <Dialog 
        open={cloneDialogOpen} 
        onClose={handleCloneCancel}
        fullWidth 
        maxWidth="xs"
        disableEscapeKeyDown={false}
        onBackdropClick={handleCloneCancel}
      >
        <DialogTitle sx={{ pr: 6 }}>
          Dupliquer la campagne
          <IconButton
            aria-label="close"
            onClick={handleCloneCancel}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Veuillez saisir un nom pour la nouvelle campagne clonée
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Nom de la campagne"
            type="text"
            fullWidth
            variant="standard"
            value={cloneNewName}
            onChange={(e) => {
              setCloneNewName(e.target.value);
              if (e.target.value.trim()) {
                setCloneNameError(false);
              }
            }}
            error={cloneNameError}
            helperText={cloneNameError ? "Le nom de la campagne ne peut pas être vide" : ""}
            sx={{ fontFamily: 'Montserrat, Arial, sans-serif' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloneCancel}>Annuler</Button>
          <Button onClick={handleCloneConfirm} variant="contained">Dupliquer</Button>
        </DialogActions>
      </Dialog>
      
      {/* Quota Exceeded Modal */}
      <Dialog 
        open={quotaExceededOpen} 
        onClose={() => setQuotaExceededOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          Quota quotidien atteint
          <IconButton
            aria-label="close"
            onClick={() => setQuotaExceededOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {quotaErrorMessage}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Votre plan actuel limite le nombre de requêtes chat par jour. Pour continuer à utiliser le chat IA sans restriction, veuillez mettre à niveau votre abonnement.
          </Typography>
          <Box sx={{ 
            p: 2, 
            backgroundColor: 'background.paper', 
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              Plans disponibles avec plus de requêtes chat :
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              • Growth : 10 requêtes/jour
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              • Pro : 30 requêtes/jour
            </Typography>
            <Typography variant="body2">
              • Enterprise : Requêtes illimitées
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuotaExceededOpen(false)}>
            Plus tard
          </Button>
          <Button 
            onClick={() => {
              setQuotaExceededOpen(false);
              navigate('/preferences');
            }}
            variant="contained"
            color="primary"
          >
            Mettre à niveau
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Chat;